import { ref, readonly } from 'vue';

// Module-level state — every useEntityCelebration() caller sees the same
// signal so FullLayout's toast can react regardless of where the celebration
// is fired from.
const celebrationActive = ref(false);
const currentMilestone = ref<Milestone | null>(null);
let dismissTimer: ReturnType<typeof setTimeout> | null = null;

export type EntityKind =
  | 'project'
  | 'customer'
  | 'team_member'
  | 'estimate'
  | 'document'
  | 'takeoff';

export interface Milestone {
  kind: EntityKind;
  /** What the user just did, in second-person */
  title: string;
  /** A nudge for the obvious next action */
  nextAction?: string;
  /** Vue-router path for the next action */
  nextActionRoute?: string;
  /** mdi icon name for the toast */
  icon: string;
  /** Color hint for the toast accent */
  color: 'success' | 'primary' | 'warning';
}

const MILESTONES: Record<EntityKind, Omit<Milestone, 'kind'>> = {
  project: {
    title: 'You created your first project! 🎉',
    nextAction: 'Invite a teammate',
    nextActionRoute: '/team-tracker',
    icon: 'mdi-briefcase-check-outline',
    color: 'success',
  },
  customer: {
    title: 'First customer added — nice!',
    nextAction: 'Create a project for them',
    nextActionRoute: '/projects',
    icon: 'mdi-account-check-outline',
    color: 'success',
  },
  team_member: {
    title: 'Your team is growing 👥',
    nextAction: 'Show them around with a tour',
    nextActionRoute: '/help-center',
    icon: 'mdi-account-multiple-plus-outline',
    color: 'success',
  },
  estimate: {
    title: 'First estimate built — solid.',
    nextAction: 'Send it to a customer',
    icon: 'mdi-file-document-check-outline',
    color: 'primary',
  },
  document: {
    title: 'First document uploaded',
    nextAction: 'Share it with your team',
    icon: 'mdi-file-upload-outline',
    color: 'primary',
  },
  takeoff: {
    title: 'First takeoff complete!',
    nextAction: 'Generate an estimate from it',
    nextActionRoute: '/estimates',
    icon: 'mdi-ruler',
    color: 'success',
  },
};

/**
 * Idempotent — only fires once per entity kind per user (gated via localStorage).
 * Safe to call from many places (toast handler, analytics, store action).
 */
function celebrate(kind: EntityKind) {
  const flagKey = `bs_celebrated_${kind}`;
  if (typeof window === 'undefined') return;
  if (localStorage.getItem(flagKey)) return;
  localStorage.setItem(flagKey, String(Date.now()));

  currentMilestone.value = { kind, ...MILESTONES[kind] };
  celebrationActive.value = true;

  // Auto-dismiss after 7 seconds (longer than usual toast — this is a moment)
  if (dismissTimer) clearTimeout(dismissTimer);
  dismissTimer = setTimeout(() => {
    celebrationActive.value = false;
  }, 7000);
}

function dismiss() {
  celebrationActive.value = false;
  if (dismissTimer) {
    clearTimeout(dismissTimer);
    dismissTimer = null;
  }
}

// Reset for QA / demos. Available in dev only via window.resetCelebrations().
if (typeof window !== 'undefined' && (import.meta as any).env?.DEV) {
  (window as any).resetCelebrations = () => {
    (Object.keys(MILESTONES) as EntityKind[]).forEach((k) =>
      localStorage.removeItem(`bs_celebrated_${k}`),
    );
    // eslint-disable-next-line no-console
    console.info('[useEntityCelebration] all milestone flags cleared');
  };
}

export function useEntityCelebration() {
  return {
    celebrationActive: readonly(celebrationActive),
    currentMilestone: readonly(currentMilestone),
    celebrate,
    dismiss,
  };
}
